import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  ToggleLeft,
  ToggleRight,
  Tag,
  TrendUp,
  Calendar,
  CurrencyDollar,
} from '@phosphor-icons/react';
import { useDiscounts, useDeleteDiscount, useToggleDiscount } from '../../hooks/useDiscounts';
import { confirmAction } from '../../components/modals/ConfirmModal';
import {
  Discount,
  DiscountType,
  getDiscountTypeLabel,
  getDiscountScopeLabel,
  formatDiscountValue,
  getDiscountStatus,
  getStatusColor,
} from '../../types/discount';

export default function Discounts() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'expired' | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<DiscountType | 'ALL'>('ALL');
  const [page, setPage] = useState(1);

  const { data, error, isLoading } = useDiscounts({
    status: statusFilter,
    type: typeFilter !== 'ALL' ? typeFilter : undefined,
    search: searchQuery,
    page,
    limit: 20,
  });

  const deleteMutation = useDeleteDiscount();

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirmAction({
      title: 'Delete Discount',
      message: `Are you sure you want to delete the discount "${name}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(id);
    } catch {
      alert('Failed to delete discount');
    }
  };

  const discounts = data?.data || [];
  const pagination = data?.pagination;

  const getStatusBadge = (discount: Discount) => {
    const status = getDiscountStatus(discount);
    const colorClass = getStatusColor(status);
    return (
      <span className={`${colorClass} rounded px-2 py-0.5 text-[10px] font-bold uppercase`}>
        {status}
      </span>
    );
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl p-4 text-red-800 dark:text-red-300 text-xs font-semibold">
        Failed to load promotional discount campaigns. Please try refreshing.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* High-density Polaris Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm">
        <div>
          <h1 className="text-base font-semibold text-[#202223] dark:text-white">
            Discounts & Promotional Offers
          </h1>
        </div>
        <Link to="/discounts/new">
          <button className="h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 shadow-xs transition-colors">
            <Plus weight="bold" className="w-3.5 h-3.5" />
            Create Discount
          </button>
        </Link>
      </div>

      {/* KPI Metric Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-medium text-[#5C5F62] dark:text-slate-400">
              Total Campaigns
            </span>
            <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
              {pagination?.total || 0}
            </p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#2C6ECB]">
            <Tag weight="fill" className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-medium text-[#5C5F62] dark:text-slate-400">
              Active Offers
            </span>
            <p className="text-xl font-bold text-[#006838] font-mono mt-1">
              {discounts.filter((d) => getDiscountStatus(d) === 'active').length}
            </p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-[#E3F1DF] text-[#006838] flex items-center justify-center">
            <TrendUp weight="fill" className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-medium text-[#5C5F62] dark:text-slate-400">
              Scheduled
            </span>
            <p className="text-xl font-bold text-[#2C6ECB] font-mono mt-1">
              {discounts.filter((d) => getDiscountStatus(d) === 'upcoming').length}
            </p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#2C6ECB]">
            <Calendar weight="fill" className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-medium text-[#5C5F62] dark:text-slate-400">
              Expired Offers
            </span>
            <p className="text-xl font-bold text-red-600 font-mono mt-1">
              {discounts.filter((d) => getDiscountStatus(d) === 'expired').length}
            </p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-600 flex items-center justify-center">
            <CurrencyDollar weight="fill" className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Filter Bar & Data Table Container */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        {/* Search & Filters */}
        <div className="p-3.5 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2 relative">
            <MagnifyingGlass
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
            />
            <input
              type="text"
              placeholder="Search discount campaigns by name or coupon code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white placeholder-[#8C9196] focus:outline-none focus:ring-1 focus:ring-[#006838]"
            />
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="all">All Campaign Statuses</option>
              <option value="active">Active Now</option>
              <option value="inactive">Paused / Inactive</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          <div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="ALL">All Incentive Types</option>
              <option value={DiscountType.PERCENTAGE}>Percentage Off</option>
              <option value={DiscountType.FIXED_AMOUNT}>Fixed Amount</option>
              <option value={DiscountType.BUY_X_GET_Y}>Buy X Get Y</option>
              <option value={DiscountType.FREE_SHIPPING}>Free Shipping</option>
            </select>
          </div>
        </div>

        {/* Table Content */}
        {isLoading ? (
          <div className="p-12 space-y-3 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg" />
            ))}
          </div>
        ) : discounts.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Tag weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
              No Promotional Discounts Found
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 max-w-sm mx-auto">
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'ALL'
                ? 'Try clearing your search or status filters to display other discounts.'
                : 'Create your first discount campaign to boost conversions.'}
            </p>
            {!searchQuery && statusFilter === 'all' && typeFilter === 'ALL' && (
              <div className="mt-4">
                <Link to="/discounts/new">
                  <button className="h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 shadow-xs">
                    <Plus weight="bold" className="w-3.5 h-3.5" />
                    Create Discount
                  </button>
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                  <th className="py-2.5 px-4">Discount Name</th>
                  <th className="py-2.5 px-4">Coupon Code</th>
                  <th className="py-2.5 px-4">Type</th>
                  <th className="py-2.5 px-4">Value</th>
                  <th className="py-2.5 px-4">Scope</th>
                  <th className="py-2.5 px-4">Usage</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {discounts.map((discount) => (
                  <DiscountRow
                    key={discount.id}
                    discount={discount}
                    onDelete={handleDelete}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="border-t border-[#E1E4E8] dark:border-slate-800 px-4 py-3 flex items-center justify-between text-xs text-[#5C5F62]">
            <span>
              Showing {(page - 1) * (pagination.limit || 20) + 1} to{' '}
              {Math.min(page * (pagination.limit || 20), pagination.total)} of {pagination.total} discounts
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-7 px-3 rounded border border-[#D2D5D9] bg-white hover:bg-[#F6F6F7] disabled:opacity-50 font-semibold text-xs"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pagination.totalPages}
                className="h-7 px-3 rounded border border-[#D2D5D9] bg-white hover:bg-[#F6F6F7] disabled:opacity-50 font-semibold text-xs"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DiscountRow({
  discount,
  onDelete,
  getStatusBadge,
}: {
  discount: Discount;
  onDelete: (id: string, name: string) => void;
  getStatusBadge: (discount: Discount) => JSX.Element;
}) {
  const toggleMutation = useToggleDiscount();

  const handleToggle = async () => {
    try {
      await toggleMutation.mutateAsync({ id: discount.id, active: !discount.active });
    } catch {
      alert('Failed to toggle discount status');
    }
  };

  return (
    <tr className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors">
      <td className="py-3 px-4">
        <div>
          <p className="font-semibold text-[#202223] dark:text-white">{discount.name}</p>
          {discount.description && (
            <p className="text-[11px] text-[#5C5F62] dark:text-slate-400 mt-0.5 line-clamp-1">
              {discount.description}
            </p>
          )}
        </div>
      </td>

      <td className="py-3 px-4">
        {discount.code ? (
          <code className="bg-[#E3F1DF] px-2 py-0.5 rounded text-[11px] text-[#006838] font-mono font-bold">
            {discount.code}
          </code>
        ) : (
          <span className="text-[#8C9196] text-[11px] italic">Automatic</span>
        )}
      </td>

      <td className="py-3 px-4 font-semibold text-[#202223] dark:text-slate-200">
        {getDiscountTypeLabel(discount.type)}
      </td>

      <td className="py-3 px-4 font-bold text-[#006838] font-mono">
        {formatDiscountValue(discount)}
      </td>

      <td className="py-3 px-4 text-[#5C5F62] dark:text-slate-400">
        {getDiscountScopeLabel(discount.scope)}
      </td>

      <td className="py-3 px-4 font-mono">
        <span className="font-semibold text-[#202223] dark:text-white">
          {discount.usageCount || 0}
        </span>
        {discount.usageLimit ? (
          <span className="text-[#5C5F62]"> / {discount.usageLimit}</span>
        ) : null}
      </td>

      <td className="py-3 px-4">{getStatusBadge(discount)}</td>

      <td className="py-3 px-4 text-right">
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={handleToggle}
            title={discount.active ? 'Disable campaign' : 'Enable campaign'}
            className="p-1 rounded hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
          >
            {discount.active ? (
              <ToggleRight weight="fill" className="w-5 h-5 text-[#006838]" />
            ) : (
              <ToggleLeft weight="fill" className="w-5 h-5 text-[#8C9196]" />
            )}
          </button>

          <Link
            to={`/discounts/${discount.id}/edit`}
            className="p-1 rounded hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
            title="Edit discount"
          >
            <PencilSimple weight="bold" className="w-4 h-4" />
          </Link>

          <button
            type="button"
            onClick={() => onDelete(discount.id, discount.name)}
            className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/40 text-red-600 transition-colors"
            title="Delete discount"
          >
            <Trash weight="bold" className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}
